import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, ApiError } from '@general/ui-kit'

import { collectContext } from './context'

// Which conversation was open, so a reload on a phone lands back in it — card
// and widget included, which the server keeps. Per browser and best effort: a
// private window or blocked storage simply starts with the home screen.
const LAST_KEY = 'general.assistant.conversation'
const remember = (id) => {
  try {
    if (id) localStorage.setItem(LAST_KEY, id)
    else localStorage.removeItem(LAST_KEY)
  } catch { /* storage blocked: nothing to remember */ }
}
const remembered = () => {
  try { return localStorage.getItem(LAST_KEY) } catch { return null }
}

export const useAssistantStore = defineStore('assistant', () => {
  const open = ref(false)
  const messages = ref([])
  const conversationId = ref(null)
  const conversations = ref([])
  const capabilities = ref(null)
  const sending = ref(false)
  const error = ref(null)

  // What it is doing right now, so the panel can say so instead of animating
  // three dots for ten seconds.
  const activity = ref(null)     // { type: 'thinking' } | { type: 'tool', label, module }
  const liveSteps = ref([])      // what it has consulted during THIS turn
  // La respuesta mientras se escribe (eventos `delta`). Se pinta como una
  // burbuja más y la sustituye el `done`, que trae la comprobada.
  const draft = ref('')

  // A write the assistant proposed and the user has not confirmed yet.
  const pendingAction = ref(null)
  // A question the assistant asked with a widget (ask_user), waiting.
  const widget = ref(null)
  // A field of the proposal being saved, and what the server said about it.
  const revising = ref(false)
  const reviseError = ref(null)
  // The hands-free call is open. Here and not in the panel so the call, the
  // chat and the card are one conversation: hanging up and typing carries on
  // exactly where the call left it, proposal included.
  const calling = ref(false)
  // The action the last answer belonged to, so the call's own sentences are
  // metered on it (the cost panel).
  const actionId = ref(null)
  // The last request, so «Reintentar» resends exactly it — a confirmation
  // resent gets the same answer from the server, never a second write.
  let lastBody = null

  // The last question, kept so a failed turn can be retried without retyping.
  const lastMessage = ref(null)

  // ── Asking out loud ────────────────────────────────────────────────────────
  // Transcribing, which happens before the turn and is not part of it: the
  // panel says «escuchando…» rather than «pensando…», because those are
  // different waits and one of them is the person's own recording being read.
  const listening = ref(false)
  // The recording was received and there was nothing in it. Not an error: a
  // thumb over the microphone deserves a hint, not a red banner.
  const heardNothing = ref(false)
  // Whether the question in flight was spoken, which is what decides if the
  // answer is read back. Somebody who typed did not ask to be spoken to.
  const spokeLast = ref(false)

  // Cancels the in-flight turn. A question that is going nowhere should be
  // stoppable; the alternative is a locked panel and a reload.
  let controller = null

  // Typing is allowed while a proposal or a widget waits: what is typed is a
  // correction («mejor el jueves»), and the server closes what was waiting.
  const canAsk = computed(() => !sending.value && !listening.value)
  // The microphone is NOT gated on a pending action, unlike the text box: a
  // proposal waiting for an answer is exactly when somebody wants to speak.
  const canSpeak = computed(() => !sending.value && !listening.value)
  // Whether this instance can be spoken to at all. False hides the microphone,
  // which is the honest answer on an instance with no speech provider.
  const canListen = computed(() => Boolean(capabilities.value?.canListen))
  const suggestions = computed(() => capabilities.value?.suggestions ?? [])
  const isAvailable = computed(() => capabilities.value !== null)

  async function loadCapabilities() {
    try {
      capabilities.value = await api.get('/assistant/capabilities')
    } catch {
      capabilities.value = null
    }
  }

  // ── Lo que recuerda de esta persona («Lo que recuerdo de ti») ─────────────
  // Las suyas y los nombres de la empresa; el servidor filtra por quien pide.
  const memories = ref([])
  const canShareMemories = ref(false)
  const memoriesFailed = ref(false)

  async function loadMemories() {
    memoriesFailed.value = false
    try {
      const data = await api.get('/assistant/memories')
      memories.value = data.memories ?? []
      canShareMemories.value = Boolean(data.canShareWithCompany)
    } catch {
      memories.value = []
      memoriesFailed.value = true
    }
  }

  async function forgetMemory(id) {
    await api.delete(`/assistant/memories/${encodeURIComponent(id)}`)
    memories.value = memories.value.filter(memory => memory.id !== id)
  }

  /** All of the person's own; the company's names stay. */
  async function forgetAllMemories() {
    await api.delete('/assistant/memories')
    memories.value = memories.value.filter(memory => memory.scope === 'company')
  }

  async function loadConversations() {
    try {
      const data = await api.get('/assistant/conversations')
      conversations.value = data.conversations
    } catch {
      conversations.value = []
    }
  }

  async function openConversation(id) {
    const { conversation } = await api.get(`/assistant/conversations/${id}`)
    conversationId.value = conversation.id
    messages.value = conversation.messages.map(message => ({ ...message, pending: false }))
    // What it was waiting for, as the server kept it — corrections included.
    pendingAction.value = conversation.pendingAction ?? null
    widget.value = conversation.widget ?? null
    error.value = null
    remember(conversation.id)
  }

  // ── «Hoy» (nudges/ in the backend) ─────────────────────────────────────────
  // What the modules' nudges have for this person now: `{ day, items }`, or
  // null before it is asked or where nothing answers.
  const today = ref(null)
  const todayFailed = ref(false)

  async function loadToday() {
    todayFailed.value = false
    try {
      today.value = await api.get('/assistant/today')
    } catch (failure) {
      today.value = null
      // Without the route, or without permission, the card is simply not
      // there; without a network it says so and can be retried.
      todayFailed.value = !failure?.status || failure.status >= 500
    }
  }

  // The morning summary, for this person: on unless they muted its category
  // (`assistant.digest`) in their notification preferences, which is the
  // profile's `notificationPreferences` — the same switch the bell obeys.
  const DIGEST = 'assistant.digest'
  const digestMuted = ref(false)

  const mutes = (entry) => entry?.category === DIGEST
    && !(entry.channels ?? []).some(channel => ['app', 'email', 'push'].includes(channel))

  async function loadDigestPreference() {
    try {
      const me = await api.get('/auth/me')
      const profile = me?.user ?? me
      digestMuted.value = mutes((profile?.notificationPreferences ?? []).find(entry => entry.category === DIGEST))
    } catch { /* the switch keeps what it showed */ }
  }

  async function setDigestMuted(muted) {
    const me = await api.get('/auth/me')
    const profile = me?.user ?? me
    const others = (profile?.notificationPreferences ?? []).filter(entry => entry.category !== DIGEST)
    // Muted is an empty list of channels; on again is no entry at all, so the
    // category's own default (the bell and the phone) applies.
    const notificationPreferences = muted ? [...others, { category: DIGEST, channels: [] }] : others
    await api.patch('/auth/me', { notificationPreferences })
    digestMuted.value = muted
  }

  // A question prepared for the person to read and send — «Replanificar» on a
  // late order puts it in the box and sends nothing (the panel moves it there).
  const prepared = ref(null)

  function prepare(text) {
    if (!text?.trim()) return
    prepared.value = text.trim()
    if (!open.value) toggle()
  }

  /** The morning notification's link: the assistant open on its first screen. */
  function openToday() {
    if (messages.value.length) newConversation()
    if (!open.value) toggle()
    loadToday()
  }

  function newConversation() {
    conversationId.value = null
    messages.value = []
    pendingAction.value = null
    widget.value = null
    error.value = null
    liveSteps.value = []
    remember(null)
  }

  /**
   * One turn, reported as it happens.
   *
   * The stream is the normal path; a browser or proxy that cannot do
   * text/event-stream falls back to the plain POST, which returns exactly the
   * same object at the end. The panel cannot tell the difference apart from
   * the progress it does or does not see.
   */
  async function send(text, { spoken = null } = {}) {
    if (!text?.trim() || sending.value) return

    lastMessage.value = text
    spokeLast.value = Boolean(spoken)
    // Whatever was waiting is answered by these words; the server says so in
    // the history. Dropped here so the screen does not offer it any more.
    pendingAction.value = null
    widget.value = null
    // What goes with the words (a photo, a PDF, an email), taken off the
    // composer now: it belongs to this message.
    const attaching = attachments.value
    attachments.value = []
    attachOpen.value = false
    // `spoken` carries the recording so the thread shows what was sent as a
    // voice note AND what was heard from it. Both, deliberately: the note is
    // the proof and the transcript is the part that can be wrong.
    messages.value.push({
      role: 'user', text, spoken,
      ...(attaching.length ? { attachments: attaching.map(({ kind, name, url, mimeType }) => ({ kind, name, url, mimeType })) } : {}),
    })
    beginTurn()

    // The files go up first, through the door every upload goes through, and
    // only their ids travel with the question. If one is refused the question
    // is not sent: it would be answered without what it was about.
    let sent = []
    try {
      sent = await uploadAttachments(attaching)
    } catch (problem) {
      messages.value.pop()
      attachments.value = attaching
      error.value = problem
      sending.value = false
      activity.value = null
      controller = null
      return
    }

    const body = {
      message: text,
      conversationId: conversationId.value,
      // A snapshot at the moment of asking: "this one" refers to now.
      screenContext: collectContext(),
      locale: document.documentElement.lang || 'es',
      ...(sent.length ? { attachments: sent } : {}),
    }

    await run(body)
  }

  // ── Lo que se adjunta a la pregunta (backend/attachments.js) ──────────────
  // Photos and documents are kept as Files until the message is sent; an email
  // of the inbox is only its id — it already lives in the mail client.
  const attachments = ref([])
  const attachOpen = ref(false)
  const MAX_ATTACHMENTS = 4
  let attachmentKey = 0

  function addFiles(files) {
    const room = MAX_ATTACHMENTS - attachments.value.length
    const taken = [...(files ?? [])].slice(0, Math.max(0, room)).map(file => ({
      key: `a${++attachmentKey}`,
      kind: 'file',
      file,
      name: file.name,
      mimeType: file.type,
      // For the thumbnail in the composer and in the thread of this session.
      url: String(file.type).startsWith('image/') ? URL.createObjectURL(file) : null,
    }))
    attachments.value = [...attachments.value, ...taken]
  }

  function removeAttachment(key) {
    const gone = attachments.value.find(item => item.key === key)
    if (gone?.url) URL.revokeObjectURL(gone.url)
    attachments.value = attachments.value.filter(item => item.key !== key)
  }

  /**
   * «Pedir al asistente» on an email: the panel opens with the email attached
   * and, when the mail screen sends one, a question written in the box.
   */
  function attachEmail({ id, subject = '', text = '' } = {}) {
    if (!id) return
    if (!attachments.value.some(item => item.kind === 'email' && item.id === id)) {
      attachments.value = [...attachments.value, { key: `a${++attachmentKey}`, kind: 'email', id, name: subject || '✉' }].slice(-MAX_ATTACHMENTS)
    }
    if (text) prepare(text)
    else if (!open.value) toggle()
  }

  async function uploadAttachments(items) {
    const sent = []
    for (const item of items) {
      if (item.kind === 'email') { sent.push({ kind: 'email', id: item.id }); continue }
      const form = new FormData()
      form.append('file', item.file, item.file.name)
      const { attachment } = await api.post('/assistant/attachments', form)
      sent.push({ kind: 'file', id: attachment.id })
    }
    return sent
  }

  // The mail client (or any screen) asks without importing the assistant: a
  // window event, so neither package depends on the other.
  if (typeof window !== 'undefined') {
    window.addEventListener('general:assistant-attach', event => attachEmail(event.detail ?? {}))
  }

  /**
   * The answer to a widget, in one go.
   *
   * @param {object} values  { fieldId: value } — what the widget collected
   * @param {string} said    the same answer in words, for the thread
   */
  async function answerWidget(values, said) {
    if (!widget.value || sending.value) return
    const question = widget.value
    widget.value = null
    if (said) messages.value.push({ role: 'user', text: said })
    beginTurn()
    await run({
      answer: { id: question.id, values },
      conversationId: conversationId.value,
      screenContext: collectContext(),
      locale: document.documentElement.lang || 'es',
    })
  }

  /**
   * A field of the proposal corrected on its card.
   *
   * The server writes it into the proposal AND into the conversation, and
   * sends the card back redrawn — summary, notes and all — so nothing here
   * recomputes what the module decides.
   */
  async function revisePending(changes) {
    if (!pendingAction.value || revising.value) return false
    revising.value = true
    reviseError.value = null
    try {
      const result = await api.patch(`/assistant/pending/${encodeURIComponent(pendingAction.value.id)}`, {
        changes,
        conversationId: conversationId.value,
      })
      pendingAction.value = result.pendingAction
      return true
    } catch (problem) {
      reviseError.value = problem
      return false
    } finally {
      revising.value = false
    }
  }

  /**
   * A turn of the call, folded into the same conversation: what was heard
   * goes in the thread as a spoken message, and the answer like any other.
   */
  function applyCallTurn(result) {
    if (result.transcript) messages.value.push({ role: 'user', text: result.transcript, spokenInCall: true })
    apply(result, { refresh: false })
  }

  /**
   * A recording, turned into the same question anybody could have typed.
   *
   * Two steps on purpose. The words come back first and are sent down the
   * ordinary text path, so speaking changes how a question is written and
   * nothing about what happens to it — same loop, same tools, same
   * confirmation card in front of anything that writes.
   *
   * @param {File|Blob} file  what `UiAudioRecorder` produced
   */
  async function sendVoice(file) {
    if (!file || !canSpeak.value) return

    listening.value = true
    heardNothing.value = false
    error.value = null

    try {
      const form = new FormData()
      form.append('audio', file, file.name ?? 'question.webm')
      form.append('locale', document.documentElement.lang || 'es')

      const heard = await api.post('/assistant/chat/voice', form)

      if (!heard.transcript) { heardNothing.value = true; return }

      // Played back from the browser's own copy. The recording is not stored
      // anywhere: it is the transcript that gets kept, in the conversation, as
      // the question — which is also the only part anybody rereads.
      const spoken = { url: URL.createObjectURL(file), seconds: heard.seconds }

      // ── Answering a proposal, too ──────────────────────────────────────
      //
      // A «sí», a «no» or a correction go down as the words they were, like
      // anything typed: the server reads them against what it has waiting.
      // A «sí» used to be sent here as a confirmation of the card, and that
      // skipped the rules that know when the card is still asking («¿para
      // qué día?») or has a doubt open — there the «sí» answers the question
      // and creates nothing.
      //
      // `{ spoken }` and not `spoken`: send() destructures its options, and
      // the bare recording has no `spoken` key, so the note vanished from the
      // thread and the answer was not read back.
      await send(heard.transcript, { spoken })
    } catch (problem) {
      error.value = problem
    } finally {
      listening.value = false
    }
  }

  /**
   * Runs the action the assistant proposed. Nothing is written until this is
   * called, which is what "it acts as you, not over you" means in practice.
   */
  /**
   * @param {number|null} [item]  in a list of writes, the one to run now;
   *        without it, every one still waiting, in order
   */
  async function confirmAction(item = null) {
    if (!pendingAction.value || sending.value) return

    const action = pendingAction.value
    pendingAction.value = null
    beginTurn()

    await run({
      confirm: action.id,
      ...(Number.isInteger(item) ? { item } : {}),
      conversationId: conversationId.value,
      screenContext: collectContext(),
      locale: document.documentElement.lang || 'es',
    })
  }

  /** A write taken off a list before confirming it; the card comes back redrawn. */
  async function dropItem(index) {
    if (!pendingAction.value || revising.value) return false
    revising.value = true
    reviseError.value = null
    try {
      const result = await api.patch(`/assistant/pending/${encodeURIComponent(pendingAction.value.id)}`, {
        drop: index,
        conversationId: conversationId.value,
      })
      pendingAction.value = result.pendingAction
      return true
    } catch (problem) {
      reviseError.value = problem
      return false
    } finally {
      revising.value = false
    }
  }

  // ── Deshacer ────────────────────────────────────────────────────────────
  // The ids being undone right now, and what the server said when it could
  // not. On the outcome itself: the same object the thread draws.
  const undoing = ref([])

  /**
   * «Deshacer» under an outcome (or one row of a list's outcome). The server
   * decides whether it still can; the outcome is marked with its answer.
   */
  async function undo(outcome) {
    const id = outcome?.undo?.id
    if (!id || undoing.value.includes(id)) return
    undoing.value = [...undoing.value, id]
    try {
      const result = await api.post(`/assistant/undo/${encodeURIComponent(id)}`, {})
      outcome.undone = Boolean(result.outcome?.ok)
      outcome.undoResult = result.outcome ?? null
    } catch (problem) {
      outcome.undoResult = { ok: false, error: problem?.message ?? null, errorCode: problem?.code ?? null, errorDetails: problem?.details ?? null, expired: problem?.status === 404 }
    } finally {
      undoing.value = undoing.value.filter(entry => entry !== id)
    }
  }

  function beginTurn() {
    sending.value = true
    error.value = null
    activity.value = { type: 'thinking' }
    liveSteps.value = []
    draft.value = ''
    controller = new AbortController()
  }

  async function run(body) {
    lastBody = body
    try {
      let finished = null

      await api.stream('/assistant/chat/stream', body, {
        signal: controller.signal,
        onEvent: (event) => {
          switch (event.type) {
            case 'thinking':
              activity.value = { type: 'thinking' }
              break

            case 'tool':
              activity.value = { type: 'tool', tool: event.tool, label: event.label, labelKey: event.labelKey ?? null, module: event.module }
              break

            case 'tool_done':
              liveSteps.value.push({ tool: event.tool, module: event.module, label: event.label, labelKey: event.labelKey ?? null, ok: event.ok })
              break

            // El texto según lo escribe el modelo. `reset`: lo que había no
            // va a ser la respuesta (pidió herramientas, o el servidor la
            // rechazó y pregunta otra vez) y se retira.
            case 'delta':
              draft.value = event.reset ? '' : draft.value + String(event.text ?? '')
              break

            case 'done':
              finished = event
              break

            case 'error':
              // Carries the catalogue code, so it translates like any other.
              throw new ApiError(event.message, 500, event.code, {
                details: event.details,
                requestId: event.requestId,
              })

            default:
              break
          }
        },
      })

      // A stream that closes with neither `done` nor `error` is a turn that
      // went nowhere — a dropped connection, a proxy that gave up. Saying
      // nothing left the panel looking frozen; the red box offers a retry.
      if (finished) apply(finished)
      else if (!controller?.signal.aborted) throw new ApiError('The answer did not arrive', 0, 'NETWORK_ERROR')
    } catch (err) {
      // Stopping on purpose is not a failure worth a red box. What it had
      // written by then stays on screen, marked as cut: it was read already.
      if (err?.code === 'ABORTED' || err?.name === 'AbortError') {
        if (draft.value.trim()) messages.value.push({ role: 'assistant', text: draft.value, stopped: true })
      } else {
        error.value = err
      }
    } finally {
      draft.value = ''
      sending.value = false
      activity.value = null
      controller = null
    }
  }

  function apply(result, { refresh = true } = {}) {
    conversationId.value = result.conversationId ?? conversationId.value
    remember(conversationId.value)
    actionId.value = result.actionId ?? null
    if (result.answer || result.outcome || result.reply) {
      messages.value.push({
        role: 'assistant',
        text: result.answer,
        steps: result.steps?.length ? result.steps : null,
        // What really happened, from the module: «OT-0003 creada» and its link.
        outcome: result.outcome ?? null,
        // A query the rules answered: a Phrase the panel translates.
        reply: result.reply ?? null,
      })
    }
    if (result.cancelled) messages.value.push({ role: 'assistant', text: null, rejected: true })
    pendingAction.value = result.pendingAction ?? null
    widget.value = result.widget ?? null
    if (refresh) loadConversations()
  }

  function stop() {
    controller?.abort()
  }

  function retry() {
    if (sending.value) return
    // A confirmation or a widget answer is resent as it was: the server
    // answers a confirmation it already ran with the same result.
    // So is a question with attachments: they are already uploaded.
    if (lastBody && (lastBody.confirm || lastBody.answer || lastBody.attachments?.length)) {
      beginTurn()
      run(lastBody)
      return
    }
    if (!lastMessage.value) return
    // The failed question is already in the thread; asking again should not
    // duplicate it.
    if (messages.value.at(-1)?.role === 'user') messages.value.pop()
    send(lastMessage.value)
  }

  function rejectAction() {
    pendingAction.value = null
    messages.value.push({ role: 'assistant', text: null, rejected: true })
  }

  async function remove(id) {
    await api.delete(`/assistant/conversations/${id}`)
    if (conversationId.value === id) newConversation()
    loadConversations()
  }

  /** The person's whole history, theirs only: the server filters by who asks. */
  async function removeAll() {
    await api.delete('/assistant/conversations')
    newConversation()
    conversations.value = []
  }

  function toggle() {
    open.value = !open.value
    if (open.value && !capabilities.value) {
      loadCapabilities()
      loadConversations()
      // After a reload: back to the conversation that was open, if it still
      // exists. One that was deleted, or belongs to nobody now, is forgotten.
      const last = remembered()
      if (last && !conversationId.value && !messages.value.length) {
        openConversation(last).catch(() => remember(null))
      }
    }
  }

  return {
    open, messages, conversationId, conversations, capabilities,
    sending, error, pendingAction, canAsk, activity, liveSteps, draft,
    widget, revising, reviseError, calling, actionId,
    answerWidget, revisePending, applyCallTurn, dropItem, undo, undoing,
    suggestions, isAvailable, lastMessage,
    listening, heardNothing, spokeLast, canListen, canSpeak,
    toggle, send, sendVoice, confirmAction, rejectAction, newConversation, stop, retry,
    openConversation, loadConversations, loadCapabilities, remove, removeAll,
    memories, canShareMemories, memoriesFailed, loadMemories, forgetMemory, forgetAllMemories,
    today, todayFailed, loadToday, prepared, prepare, openToday,
    digestMuted, loadDigestPreference, setDigestMuted,
    attachments, attachOpen, addFiles, removeAttachment, attachEmail,
  }
})
